import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PageHero } from '@/components/PageHero';
import { Reveal } from '@/components/Reveal';
import { NotifyForm } from '@/components/NotifyForm';
import { STATUS_META } from '@/content/features';
import { sortedUpdates, UPDATE_AREAS } from '@/content/updates';
import { formatPostDate } from '@/content/format';
import { useSeo } from '@/seo/useSeo';

// Literal Tailwind arbitrary-value classes, not interpolated, so the JIT static scan finds them
// (same reason as lib/tone.ts and Roadmap.tsx's statusMeta).
const STATUS_CHIP_CLASS: Record<string, string> = {
  live: 'border-[#3ddc84]/45 text-[#3ddc84]',
  configure: 'border-[#b06bff]/45 text-nexus-purple-light',
  building: 'border-[#e0a62a]/45 text-[#e0a62a]',
  roadmap: 'border-white/25 text-white/55',
};

export function WhatsNew(): JSX.Element {
  const entries = sortedUpdates();

  useSeo({
    title: "What's new",
    description:
      'Every change that actually shipped to the Nexus platform, dated and described honestly — including what each one is not yet. No pre-announcements.',
    path: '/whats-new',
    schema: {
      '@type': 'ItemList',
      name: 'Nexus product updates',
      itemListElement: entries.map((u, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: u.title,
        description: u.summary,
      })),
    },
  });

  return (
    <div>
      <PageHero
        eyebrow="Product updates"
        title="What actually shipped."
        subtitle="A dated record of changes that are merged and running — written in the same vocabulary as the rest of the site, including the part where we tell you what something is not yet. Nothing here is an announcement of future work; that lives on the roadmap."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="mx-auto mb-12 flex max-w-3xl flex-wrap justify-center gap-2">
            {UPDATE_AREAS.map((area) => (
              <span
                key={area}
                className="rounded-full border border-white/12 px-3 py-1 text-[11px] font-semibold text-white/50"
              >
                {area}
              </span>
            ))}
          </div>

          <div className="relative mx-auto max-w-3xl">
            <div className="absolute left-[7px] top-3 bottom-3 w-px bg-white/10" />
            <div className="flex flex-col gap-8">
              {entries.map((u, i) => {
                const status = STATUS_META[u.status];
                return (
                  <Reveal key={u.slug} delay={(i % 3) * 70}>
                    <div className="relative flex gap-5 md:gap-6">
                      <div className="z-10 mt-6 h-[15px] w-[15px] flex-shrink-0 rounded-full border border-nexus-purple/50 bg-[#0b0b13]">
                        <div className="m-[3px] h-[7px] w-[7px] rounded-full bg-nexus-purple" />
                      </div>
                      <article
                        id={u.slug}
                        className="flex-1 scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7"
                      >
                        <div className="flex flex-wrap items-center gap-2.5">
                          <span className="text-xs font-semibold text-white/45">{formatPostDate(u.date)}</span>
                          <span className="text-white/20">&middot;</span>
                          <span className="text-xs text-white/45">{u.area}</span>
                          <Chip
                            label={status.grid}
                            size="small"
                            className={`h-[21px] border bg-transparent text-[10.5px] font-bold ${
                              STATUS_CHIP_CLASS[u.status]
                            }`}
                          />
                        </div>

                        <h2 className="mt-3 font-display text-xl font-bold leading-snug text-nexus-ink md:text-[23px]">
                          {u.title}
                        </h2>
                        <p className="mt-3 text-[15px] leading-relaxed text-white/70">{u.summary}</p>

                        <ul className="mt-5 flex flex-col gap-2.5">
                          {u.highlights.map((h) => (
                            <li key={h} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/65">
                              <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple/70" />
                              {h}
                            </li>
                          ))}
                        </ul>

                        {u.caveat && (
                          <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#e0a62a]/25 bg-[#e0a62a]/[0.06] p-4">
                            <InfoOutlinedIcon sx={{ fontSize: 17, color: '#e0a62a', marginTop: '1px' }} />
                            <p className="text-[13px] leading-relaxed text-white/70">
                              <span className="font-semibold text-[#e0a62a]">What this is not yet: </span>
                              {u.caveat}
                            </p>
                          </div>
                        )}

                        {u.relatedTo && u.relatedTo.length > 0 && (
                          <div className="mt-5 flex flex-wrap gap-2.5">
                            {u.relatedTo.map((r) => (
                              <Link
                                key={r.to}
                                to={r.to}
                                className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-white/70 no-underline transition-colors hover:border-nexus-purple/50 hover:text-nexus-purple-light"
                              >
                                {r.label} &rarr;
                              </Link>
                            ))}
                          </div>
                        )}
                      </article>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <p className="mx-auto mt-12 max-w-3xl text-center text-sm text-white/45">
            Looking for what comes next instead of what already landed?{' '}
            <Link to="/roadmap" className="text-nexus-purple-light no-underline hover:underline">
              See the roadmap
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Want these as they ship?
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              We&apos;ll reach out as Nexus moves into private beta — no spam, no lists sold.
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
